/***
 *
 *   VIEW CUSTOMER DATA
 *   View the customer provided data stored for an order line item,
 *   similar to the View CAID Scope page for generated data
 *
 **********/

import React, {Fragment, useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {AppLayoutContext, Button, Form, Icon, Loader, ReactTable, useAPI} from 'components/lib';
import {CSVLink} from "react-csv";
import axios from "axios";
import ConfirmationModal from "../dataspec/shared/ConfirmationModal";

export function ViewCustomerData(props){

  const { orderId, orderItemId } = useParams();

  const orderFromApi = useAPI('/api/orders/' + orderId);
  const orderItemsFromApi = useAPI('/api/orderItem/' + orderId);

  const [rows, setRows] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [dataColumns, setDataColumns] = useState([]);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // separator options for the CSV export (same behaviour as CAID page)
  const [separatorOption, setSeparatorOption] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dataDeleted, setDataDeleted] = useState(false);
  const [delimiter, setDelimiter] = useState('Comma');
  const [otherDelimiter, setOtherDelimiter] = useState('');
  const [quote, setQuote] = useState(null);
  const delimiterOpts = {'Tab': "\t", 'Comma': ',', 'Semicolon': ';', 'Space': ' '};

  // Add buttons to nav header (needed even if empty, to clear stale buttons from prev pages)
  const [navButtons, setNavButtons] = useContext(AppLayoutContext);
  useEffect(() => {
    setNavButtons([]);
  }, []);

  useEffect(() => {

    axios.get('/api/customerData/' + orderItemId)
        .then(res => {
          setRows(res.data.data);
          setTotalRows(res.data.total ?? res.data.data.length);
          setDataColumns(res.data.columns);
        })
        .catch(err => setError(err.response?.data?.message || 'Failed to load customer provided data.'));

  }, [orderItemId]);

  const orderItemData = orderItemsFromApi.data?.find(i => i.id === orderItemId);

  const filteredRows = React.useMemo(() => {

    if (!rows) return [];
    if (!searchTerm) return rows;

    const term = searchTerm.toLowerCase();
    return rows.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(term)));

  }, [rows, searchTerm]);

  // all rows are shown — the table paginates them client side
  const previewRows = filteredRows;

  const columns = React.useMemo(() => [

    {
      Header: '#',
      id: 'row_index',
      accessor: (row) => row.row_index,
    },

    ...dataColumns.map(col => ({
      Header: col === 'uid' ? 'UID(Provide By factory)' : col === 'system_id' ? 'System ID' : col,
      id: col,
      accessor: (row) => {
        const value = String(row[col] ?? '');
        return value.length > 130 ? value.slice(0, 130) + '...' : value;
      },
    })),

  ], [dataColumns]);

  return (
      <Fragment>

        { orderFromApi.loading || orderItemsFromApi.loading || (rows == null && !error) ?
            <Loader /> :
            <>
              {/* Breadcrumbs */}
              <div className={"flex mb-4 -mt-2"}>
                <a className={"relative text-sm hover:underline text-orange-500"}
                   href={`/orders`}>
                  Orders
                </a>
                <span className={"mx-2 text-sm text-gray-400"}> / </span>
                <a className={"relative text-sm hover:underline text-orange-500"}
                   href={`/order/${orderId}`}>
                  {orderFromApi.data?.vtiger_salesorder_no || 'Order'}
                </a>
                <span className={"mx-2 text-sm text-gray-400"}> / </span>
                <p className={"relative text-sm text-gray-500"}>
                  Customer Provided Data
                </p>
              </div>

              { error &&
                  <p className={"text-sm text-red-500 mb-4"}>{error}</p> }

              <div className={"flex mb-4"}>

                {/* Details */}
                <div className={"w-72 space-y-2 shadow bg-gray-50 rounded-lg p-4"}>
                  <h3 className={"text-xl font-semibold mb-3 leading-6 text-gray-900"}>Details</h3>

                  <div className={"flex gap-8"}>
                    <div className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>Line Item</div>
                      <div className={"text-gray-500"}>{orderItemData?.number ?? '-'}</div>
                    </div>
                    <div className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>Quantity</div>
                      <div className={"text-gray-500"}>{orderItemData?.quantity?.toLocaleString() ?? '-'}</div>
                    </div>
                  </div>

                  <div className={"flex-col mt-2"}>
                    <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>Job Name</div>
                    <div className={"text-gray-500"}>{orderItemData?.job_name || '-'}</div>
                  </div>

                  <div className={"flex-col mt-2"}>
                    <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>Total Rows</div>
                    <div className={"text-gray-500"}>{rows ? totalRows.toLocaleString() : '-'}</div>
                  </div>

                  <div className={"flex-col mt-2"}>
                    <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>Uploaded File</div>
                    { orderItemData?.data_file_upload_weblink && !dataDeleted ?
                        <a className={"relative block text-sm hover:underline text-orange-500"}
                           target="_blank"
                           rel="noreferrer"
                           href={orderItemData.data_file_upload_weblink}>
                          Open in Google Drive
                        </a> :
                        <div className={"text-gray-500"}>-</div> }
                  </div>
                </div>

                {/* Search */}
                <div className={"ml-4 flex-1 space-y-2 shadow bg-gray-50 rounded-lg p-4 max-w-lg"}>
                  <h3 className={"text-xl font-semibold mb-3 leading-6 text-gray-900"}>Search Data</h3>
                  <input
                      type="text"
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') setSearchTerm(searchValue); }}
                      placeholder="Search for matching values"
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-400"
                  />
                  <Button
                      text="Search"
                      action={() => setSearchTerm(searchValue)}
                      small
                      fullWidth
                      className="bg-orange-500 hover:bg-orange-700 w-full"
                  />
                  { searchTerm &&
                      <p className={"text-sm text-gray-400"}>
                        {filteredRows.length.toLocaleString()} row{filteredRows.length === 1 ? '' : 's'} matching "{searchTerm}" —&nbsp;
                        <span className={"text-orange-500 hover:underline cursor-pointer"}
                              onClick={() => { setSearchTerm(''); setSearchValue(''); }}>
                          clear
                        </span>
                      </p> }
                </div>

              </div>

              {/* Data */}
              <div className={"space-y-2 shadow bg-gray-50 rounded-lg p-3"}>
                <div className={"flex flex-col mb-4"}>

                  <div className={"flex"}>
                    <div className={"flex flex-col"}>
                      <div className={"text-xl font-semibold leading-6 text-gray-900"}>
                        Customer Data
                      </div>
                      <div className={"text-sm text-gray-400"}>
                        {totalRows.toLocaleString()} row{totalRows === 1 ? '' : 's'}
                      </div>
                    </div>
                    { rows && rows.length > 0 &&
                        <button
                            type="button"
                            className="ml-6 inline-flex align-top items-center bg-gray-50 rounded-md text-md font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-200"
                            onClick={() => setShowDeleteModal(true)}
                        >
                          <Icon image={'trash'} size={18} className={"mr-1"}/>
                          Delete Data
                        </button>
                    }
                  </div>

                  <div className={"flex mt-4"}>
                    <CSVLink
                        className={"cursor-pointer align-top font-sans text-sm w-fit h-12 bg-orange-500 hover:bg-orange-700 relative text-center font-semibold rounded px-12 py-3 ease-in-out duration-500 text-white"}
                        filename={`customer_data_${orderFromApi.data?.vtiger_salesorder_no || orderId}_line_item_${orderItemData?.number ?? ''}`}
                        data={previewRows.map(row => {
                          const out = {};
                          dataColumns.forEach(col => { out[col] = row[col] ?? ''; });
                          return out;
                        })}
                        separator={delimiter === "Other" ? otherDelimiter : delimiterOpts[delimiter]}
                        enclosingCharacter={quote === null ? '' : quote}
                    >
                      Export as CSV
                    </CSVLink>
                  </div>

                  <div className={"flex"}>
                    <div>
                      <Form
                          updateOnChange={true}
                          onChange={(e) => {
                            setSeparatorOption(e.value);
                          }}
                          className={"p-2 rounded-sm w-72 inline-block"}
                          flex={false}
                          data={{
                            separatorOptions: {
                              label: 'Separator Options',
                              type: 'singleCheckbox',
                              value: separatorOption,
                              required: false,
                              errorMessage: 'Please select an option',
                            },
                          }}>
                      </Form>

                      { separatorOption &&
                          <div className={"SeparatorOption mb-4"}>
                            <Form
                                updateOnChange={true}
                                onChange={(e) => {
                                  if (e.input === 'separator_ss') {
                                    setDelimiter(e.value);
                                    if (e.value !== 'Other') setOtherDelimiter('');
                                  }
                                  if (e.input === 'otherSeperator') setOtherDelimiter(e.value);
                                  if (e.input === 'quote') setQuote(e.value === '' ? null : e.value);
                                }}
                                data={{
                                  separator_ss: {
                                    type: 'radio',
                                    options: ["Tab", "Comma", "Semicolon", "Space", "Other"],
                                    required: false,
                                    errorMessage: 'Please select an option',
                                    value: delimiter,
                                    label: 'Separated by',
                                  },
                                  otherSeperator: {
                                    label: '',
                                    type: 'text',
                                    value: otherDelimiter,
                                    isHidden: delimiter !== "Other",
                                    required: false,
                                    errorMessage: 'Separated by',
                                  },
                                  quote: {
                                    label: 'String Delimiter',
                                    type: 'text',
                                    value: quote == null ? '' : quote,
                                    required: false,
                                    errorMessage: 'Please enter string delimiter',
                                  },
                                }}>
                            </Form>
                          </div> }
                    </div>
                  </div>

                </div>

                <ReactTable columns={columns} data={previewRows} storageKey="viewCustomerData.pageSize" />
              </div>

              <ConfirmationModal
                  open={showDeleteModal}
                  setOpen={setShowDeleteModal}
                  title="Delete customer provided data?"
                  message={`${totalRows.toLocaleString()} rows will be deleted. This action cannot be undone.`}
                  confirmText="Delete Data"
                  onConfirm={async () => {
                    await axios.delete('/api/customerData/' + orderItemId);
                    setRows([]);
                    setTotalRows(0);
                    setDataColumns([]);
                    setDataDeleted(true);
                  }}
              />
            </>
        }
      </Fragment>
  )
}
